import { AppSidebar } from '../../components/AppSidebar/AppSidebar';
import { formatCents, REGION_LABELS } from '../../features/pricing';
import {
  usePriceSheet,
  type DeliveryRate,
  type PriceSheetCell,
  type PriceSheetLocation,
} from '../../features/price-sheet/api';
import './price-sheet.scss';

function RegionBadge({ region }: { region: PriceSheetLocation['region'] }) {
  return (
    <span className={`price-sheet__region price-sheet__region--${region}`}>
      {REGION_LABELS[region]}
    </span>
  );
}

function ProductCell({ cell }: { cell: PriceSheetCell | undefined }) {
  if (!cell || !cell.available) {
    return <span className="price-sheet__muted">—</span>;
  }
  if (cell.region === 'OTHER' || cell.priceCents == null) {
    return <span className="price-sheet__muted">Contact</span>;
  }
  return (
    <span className="price-sheet__price">
      {formatCents(cell.priceCents)}
      <span className="price-sheet__per">/t</span>
    </span>
  );
}

function DeliveryRateCell({ rate }: { rate: DeliveryRate | undefined }) {
  if (!rate || rate.kind === 'NONE') return <span className="price-sheet__muted">—</span>;
  if (rate.kind === 'INCLUDED') return <span className="price-sheet__muted">Included</span>;
  if (rate.kind === 'VARIES') return <span className="price-sheet__muted">Varies</span>;
  return (
    <span className="price-sheet__price">
      {formatCents(rate.rateCents)}
      <span className="price-sheet__per">/t</span>
    </span>
  );
}

export default function PriceSheetPage() {
  const { data, isLoading, isError } = usePriceSheet();

  const locations = data?.locations ?? [];
  const products = data?.products ?? [];
  const hasData = locations.length > 0 && products.length > 0;
  const anySouthern = locations.some((l) => l.region === 'SOUTHERN');

  return (
    <div className="layout">
      <AppSidebar />

      <div className="card price-sheet">
        <div className="price-sheet__header">
          <h2 className="card__title">Price Sheet</h2>
          <p className="card__subtitle">
            Estimated per-ton pricing for the products available at each of your delivery locations.
          </p>
        </div>

        {isLoading ? (
          <div className="price-sheet__state">Loading your price sheet…</div>
        ) : isError ? (
          <div className="price-sheet__state">Couldn’t load the price sheet. Please try again.</div>
        ) : !hasData ? (
          <div className="price-sheet__state">
            No priced products are available for your delivery locations yet. Contact your sales
            representative for a quote.
          </div>
        ) : (
          <>
            <div className="price-sheet__scroll">
              <table className="price-sheet__table">
                <thead>
                  <tr>
                    <th className="price-sheet__corner">Product</th>
                    {locations.map((loc) => (
                      <th key={loc.id} className="price-sheet__loc-head">
                        <div className="price-sheet__loc-name">{loc.name}</div>
                        {(loc.city || loc.state) && (
                          <div className="price-sheet__loc-sub">
                            {[loc.city, loc.state].filter(Boolean).join(', ')}
                          </div>
                        )}
                        <RegionBadge region={loc.region} />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => (
                    <tr key={p.id}>
                      <th className="price-sheet__row-head" scope="row">
                        {p.name}
                      </th>
                      {locations.map((loc) => (
                        <td key={loc.id} className="price-sheet__cell">
                          <ProductCell cell={data?.cells?.[p.id]?.[loc.id]} />
                        </td>
                      ))}
                    </tr>
                  ))}

                  {anySouthern && (
                    <tr className="price-sheet__extra-row">
                      <th className="price-sheet__row-head" scope="row">
                        Delivery rate
                        <span className="price-sheet__row-note">per ton</span>
                      </th>
                      {locations.map((loc) => (
                        <td key={loc.id} className="price-sheet__cell">
                          <DeliveryRateCell rate={data?.deliveryRateByLocation?.[loc.id]} />
                        </td>
                      ))}
                    </tr>
                  )}

                  <tr className="price-sheet__extra-row">
                    <th className="price-sheet__row-head" scope="row">
                      Fuel surcharge
                    </th>
                    {locations.map((loc) => (
                      <td key={loc.id} className="price-sheet__cell">
                        {loc.region === 'OTHER' ? (
                          <span className="price-sheet__muted">—</span>
                        ) : (
                          <span className="price-sheet__muted">Varies</span>
                        )}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>

            <ul className="price-sheet__notes">
              <li>
                <strong>Northeast</strong> prices are delivered (all-in) per ton.
              </li>
              {anySouthern && (
                <li>
                  <strong>Southern</strong> prices are per ton for the material (picked up); the
                  delivery rate is added for delivered loads.
                </li>
              )}
              <li>
                Fuel surcharge <strong>varies</strong> by delivery week and is applied at order
                time.
              </li>
              <li>These are estimates — the final invoice adjusts to the actual delivered tons.</li>
            </ul>
          </>
        )}
      </div>
    </div>
  );
}
